import React, { useState, useEffect } from 'react';
import { Mail, Calendar, TrendingUp, Clock, CheckCircle, Loader, Send, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';



const DigestPreferences = ({ user }) => {
  const [preferences, setPreferences] = useState({
    weekly_digest_enabled: true,
    include_rate_trends: true,
    include_renewal_timeline: true,
    include_recommendations: true,
    include_market_news: true,
    preferred_day: 'monday'
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  const days = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];
  
  const isPremium = user?.subscription_tier === 'premium' || user?.subscription_tier === 'pro';

  useEffect(() => {
    if (isPremium) {
      loadPreferences();
    } else {
      setLoading(false);
    }
  }, [isPremium]);

  const loadPreferences = async () => {
    try {
      const response = await api.get(`/api/digest/preferences`);
      setPreferences(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error loading digest preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await api.post(`/api/digest/preferences`, preferences);
      toast.success('Digest preferences saved!');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const sendTestDigest = async () => {
    setSendingTest(true);
    try {
      const response = await api.post(`/api/digest/send-test`, {});
      
      if (response.data.result?.status === 'success') {
        toast.success('Test digest sent to your email!');
      } else {
        toast.error('Failed to send test digest');
      }
    } catch (error) {
      console.error('Error sending test digest:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test digest');
    } finally {
      setSendingTest(false);
    }
  };

  const loadPreview = async () => {
    try {
      const response = await api.post(`/api/digest/preview`, {});
      setPreviewHtml(response.data.html);
      setShowPreview(true);
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Failed to load preview');
    }
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!isPremium) {
    return (
      <div data-testid="digest-preferences" className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Mail className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold">Weekly Digest Emails</h3>
            <p className="text-sm text-muted-foreground">Premium feature</p>
          </div>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4 text-center">
          <p className="text-muted-foreground mb-3">
            Upgrade to Premium or Pro to receive weekly digest emails with rate trends, timeline updates, and personalized recommendations.
          </p>
          <a
            href="/dashboard?tab=settings"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Upgrade Now
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 flex items-center justify-center">
        <Loader className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="digest-preferences" className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Mail className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold">Weekly Digest Emails</h3>
            <p className="text-sm text-muted-foreground">
              Personalized mortgage insights delivered to your inbox
            </p>
          </div>
        </div>
        
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={preferences.weekly_digest_enabled}
            onChange={() => togglePreference('weekly_digest_enabled')}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
        </label>
      </div>

      {preferences.weekly_digest_enabled && (
        <>
          {/* Content Selection */}
          <div className="space-y-3 mb-6">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Include in Digest
            </h4>
            
            <label className="flex items-center gap-3 p-3 bg-background rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
              <input
                type="checkbox"
                checked={preferences.include_rate_trends}
                onChange={() => togglePreference('include_rate_trends')}
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
              />
              <TrendingUp className="h-4 w-4 text-primary" />
              <div className="flex-1">
                <span className="text-sm font-medium">Rate Trends</span>
                <p className="text-xs text-muted-foreground">Weekly rate changes and market direction</p>
              </div>
            </label>
            
            <label className="flex items-center gap-3 p-3 bg-background rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
              <input
                type="checkbox"
                checked={preferences.include_renewal_timeline}
                onChange={() => togglePreference('include_renewal_timeline')}
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
              />
              <Calendar className="h-4 w-4 text-primary" />
              <div className="flex-1">
                <span className="text-sm font-medium">Renewal Timeline</span>
                <p className="text-xs text-muted-foreground">Progress and upcoming tasks for your renewal</p>
              </div>
            </label>
            
            <label className="flex items-center gap-3 p-3 bg-background rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
              <input
                type="checkbox"
                checked={preferences.include_recommendations}
                onChange={() => togglePreference('include_recommendations')}
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
              />
              <CheckCircle className="h-4 w-4 text-primary" />
              <div className="flex-1">
                <span className="text-sm font-medium">Personalized Recommendations</span>
                <p className="text-xs text-muted-foreground">Custom tips based on your mortgage situation</p>
              </div>
            </label>
          </div>

          {/* Preferred Day */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              <Clock className="h-4 w-4 inline mr-2" />
              Delivery Day
            </label>
            <select
              value={preferences.preferred_day}
              onChange={(e) => setPreferences(prev => ({ ...prev, preferred_day: e.target.value }))}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {days.map(day => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              You'll receive your digest every {preferences.preferred_day} morning
            </p>
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
        <button
          onClick={savePreferences}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {saving ? (
            <Loader className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle className="h-4 w-4" />
          )}
          Save Preferences
        </button>
        
        {preferences.weekly_digest_enabled && (
          <>
            <button
              onClick={loadPreview}
              className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors"
            >
              <Eye className="h-4 w-4" />
              Preview
            </button>
            
            <button
              onClick={sendTestDigest}
              disabled={sendingTest}
              className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg disabled:opacity-50 transition-colors"
            >
              {sendingTest ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Send Test Email
            </button>
          </>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && previewHtml && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold">Email Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-60px)]">
              <iframe
                srcDoc={previewHtml}
                title="Email Preview"
                className="w-full h-[600px] border-0"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigestPreferences;
